import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import React from "react";
import {
  ASIN_ATTRIBUTE_VALUES_REQUIRING_WARNING,
  ASIN_LIVENESS_STATUSES,
  LAUNCH_ASIN_TYPES,
  NON_CUSTOMER_FACING_LAUNCH_ASIN_TYPE_VALUES,
  UNSUPPORTED_MARKETPLACES,
} from "../../constants/launchAsinTypes";
import {
  ColumnLayout,
  ExpandableSection,
  Header,
  Spinner,
} from "@amzn/awsui-components-react";
import { capitalize, snakeToCapitalCase } from "../../util/stringUtils";
import { Icon, Popover } from "@amzn/awsui-components-react/polaris";
import { useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";

export const LaunchSummaryComponent = (props: {
  asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[];
  title: string;
  headerSize: "h1" | "h2" | "h3" | "awsui-h1-sticky";
}) => {
  const [expanded, setExpanded] = React.useState(true);
  const { isLoading } = useSelector((state: RootState) => state.porAsinList);
  const fullAsins = props.asinMarketplaces.filter(
    (e) => !e.isRestricted
  ) as FullAsinWithAudits[];
  const restrictedAsins = props.asinMarketplaces.filter(
    (e) => e.isRestricted
  ) as RestrictedAsin[];

  function buildFrequencyMap(elements: string[]): { [key: string]: number } {
    const counter: { [key: string]: number } = {};
    elements.forEach((e) => {
      // Replace undefined with 'Unknown' for user readability.
      const k = e || "Unknown";
      counter[k] ? counter[k]++ : (counter[k] = 1);
    });

    return counter;
  }

  const summaryValues: { [label: string]: { [key: string]: number } } = {
    "By ASIN Type": buildFrequencyMap(
      fullAsins.map((e) => LAUNCH_ASIN_TYPES[e.asinType])
    ),
    "By ASIN Party": buildFrequencyMap(fullAsins.map((e) => e.asinParty)),
    "By Catalog Liveness": buildFrequencyMap(
      fullAsins.map((e) => snakeToCapitalCase(e.catalogLiveness || "Unknown"))
    ),
    "By Weblab Status": buildFrequencyMap(
      fullAsins.map((e) => snakeToCapitalCase(e.makoWeblabStatus || "Inactive"))
    ),
    "By Marketplace": buildFrequencyMap(fullAsins.map((e) => e.marketplace)),
    "By Program": buildFrequencyMap(
      fullAsins.map((e) => e.programName || "Unknown")
    ),
    "By Restricted Program": buildFrequencyMap(
      restrictedAsins.map((e) => e.restrictedProgramName || "Unknown")
    ),
  };

  return (
    <ExpandableSection
      header={
        <Header variant={props.headerSize || "h2"} children={props.title} />
      }
      variant="container"
      expanded={expanded}
      onChange={(e) => setExpanded(e.detail.expanded)}
    >
      {isLoading ? (
        <Spinner size="large" />
      ) : (
        <SummaryView items={summaryValues} />
      )}
    </ExpandableSection>
  );
};

function returnUserWarning(k: string): string {
  if (ASIN_LIVENESS_STATUSES.includes(k)) {
    const partiallyLive = k == "Partially Live";
    const andOr = partiallyLive ? "or" : "and";
    const bothToFalse = partiallyLive ? "" : " (setting both to ‘false’)";
    const partialOrLive = k == "Partially Live" ? "Partially Live" : "Live";
    return `Prior to using OCL, all ASINs should be ‘Not Live’. After publishing launch dates ${andOr} launch attributes${bothToFalse}, ASINs will show as "${partialOrLive}" once attributes are reconciled in Catalog. 
  Review Wiki under ‘Liveness’ for more information if values are not as expected`;
  } else if (UNSUPPORTED_MARKETPLACES.includes(k)) {
    return `${k} marketplace may require additional launch processes to take pages live. Please review Wiki under ‘Marketplace Specific Launch Processes’ for additional details.`;
  } else if (NON_CUSTOMER_FACING_LAUNCH_ASIN_TYPE_VALUES.includes(k)) {
    const asinMsg = k.includes("Certified Refurb")
      ? "ASINs are typically launched after main product launch"
      : "ASINs are not typically launched using OCL";
    return `${k} ${asinMsg}. Please ensure you wish to launch these ASINs or refine your search/filter criteria on the ASINs tab.`;
  } else {
    return `${k} fields may require further investigation before proceeding.`;
  }
}

const SummaryView = (props: {
  items: { [label: string]: { [key: string]: number } };
}) => {
  const childrenBuilder = (fmap: { [key: string]: number }) =>
    Object.keys(fmap).map((k) =>
      React.createElement(
        "div",
        null,
        <>
          <p>
            {ASIN_ATTRIBUTE_VALUES_REQUIRING_WARNING.includes(k) ? (
              <>
                <Popover
                  header={"Uncommon ASIN Type"}
                  content={
                    returnUserWarning(k)
                    // `${k} ASINs are not typically launched using OCL. Please ensure you wish to launch these ASINs or refine your search criteria in the ASINs tab.`
                  }
                >
                  <div style={{ color: "red" }}>
                    <Icon name={"status-warning"} variant={"warning"} />
                    <strong>{k}</strong> - {fmap[k]}
                  </div>
                </Popover>
              </>
            ) : (
              <>
                <strong>{k}</strong> - {fmap[k]}
              </>
            )}
          </p>
        </>
      )
    );
  const content = Object.keys(props.items).map((k) =>
    React.createElement("div", null, [
      <h3>{k}</h3>,
      ...childrenBuilder(props.items[k]),
    ])
  );
  return (
    <ColumnLayout
      columns={Object.keys(props.items).length}
      children={content}
    />
  );
};
